import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import type {
  FormatDateTimeOptions,
  TimeFormat,
  FormatDateTimeResult,
} from 'shared/types/date-time-format'
import { formatDateTimeUtil } from 'shared/utils/date-time/format-date-time'

type DateInput = Date | number | string
type Overrides = Pick<FormatDateTimeOptions, 'day' | 'locale' | 'month' | 'year'> & {
  timeFormat?: TimeFormat | null
}

/**
 * Formats a date using the current user's preferences.
 *
 * - `useFormatDateTime(date)` → date + time (user defaults)
 * - `useFormatDateTime(date, { timeFormat: null })` → date only
 * - `useFormatDateTime(date, { month: 'long' })` → date + time with override
 * - `useFormatDateTime(null | undefined)` → `null`
 *
 * Returns `{ formatted, title }` where title includes timezone identifier,
 * or `null` when `date` is nullish.
 */
export const useFormatDateTime = (
  date: DateInput | null | undefined,
  overrides?: Overrides,
): FormatDateTimeResult | null => {
  const { user } = useUser()
  const authorizedUser = isAuthorizedUser(user) ? user : undefined

  if (date == null) {
    return null
  }

  const { timeFormat: requestedTimeFormat, ...restOverrides } = overrides ?? {}
  const timeFormat =
    requestedTimeFormat === null ? undefined : (requestedTimeFormat ?? authorizedUser?.timeFormat)

  return formatDateTimeUtil(date, {
    dateFormat: authorizedUser?.dateFormat,
    timeFormat,
    timeZone: authorizedUser?.timezone,
    ...restOverrides,
  })
}
